// src/lib/errors.ts — turning database errors into sentences.
//
// Because the browser talks to Postgres directly, the errors a user hits are
// raw Postgres errors: RLS refusals, check-constraint violations, and the
// RAISE EXCEPTION messages in db/supabase-schema-steps-2-4.sql. Those messages
// were written to be read by a person, so the job here is mostly to get out of
// the way — strip the plumbing, and translate the handful of codes that arrive
// with no useful text at all.

interface MaybePostgrestError {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
}

/** Postgres error codes that reach the client with unhelpful default text. */
const CODE_MESSAGES: Record<string, string> = {
  // RLS refused the row. Usually means "you're not on this trip" — including
  // the case where someone else removed you while you had the page open.
  '42501': 'You do not have access to this trip any more. Try signing out and back in.',
  PGRST301: 'Your session has expired. Sign in again.',
  // A row the client expected exactly one of came back empty.
  PGRST116: 'That is no longer there — someone may have deleted it.',
};

function isNetworkError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('load failed') ||
    m.includes('network request failed') ||
    m.includes('aborted')
  );
}

export function describeDbError(error: unknown): string {
  if (error == null) return 'Something went wrong.';

  if (typeof error === 'string') return error;

  const err = error as MaybePostgrestError;
  const code = typeof err.code === 'string' ? err.code : '';
  const raw = typeof err.message === 'string' ? err.message : '';

  if (raw && isNetworkError(raw)) {
    // The single most likely failure on this trip, so it gets the specific
    // wording rather than "network error".
    return 'Could not reach the server. Check your connection — if you are on the VPN, it may have dropped.';
  }

  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  if (raw) {
    // Messages raised by our own functions are already plain English. Postgres
    // prefixes some of them, and PostgREST occasionally wraps the text.
    return raw
      .replace(/^ERROR:\s*/i, '')
      .replace(/^(new row|current row) violates row-level security policy.*/i,
        'You do not have permission to do that.')
      .trim();
  }

  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/** Shape every supabase-js query resolves to, before its generics get involved. */
type Result = PromiseLike<{ data: unknown; error: unknown }>;

function check(result: { data: unknown; error: unknown }): unknown {
  if (result.error) throw new Error(describeDbError(result.error));
  return result.data;
}

/**
 * Runs a query and returns its rows, or throws with a readable message.
 *
 * The type parameter is asserted rather than inferred. supabase-js infers row
 * types from generated database types, and this project has none — there is no
 * Supabase project to generate them from yet. src/data/types.ts is the
 * hand-written stand-in, and this is where it gets applied.
 */
export async function unwrap<T>(query: Result): Promise<T> {
  const data = check(await query);
  if (data == null) {
    throw new Error('The server returned nothing. It may have been deleted.');
  }
  return data as T;
}

/** For calls whose result is not used — RPCs returning void, mostly. */
export async function run(query: Result): Promise<void> {
  check(await query);
}

/**
 * For writes that must have touched a row.
 *
 * An UPDATE that RLS refuses does not error — it matches zero rows and reports
 * success, so "only the owner can rename the trip" would otherwise show up as
 * a name that quietly springs back on the next reload. Selecting the ids back
 * and insisting on at least one turns that silence into a sentence.
 */
export async function runExpectingRows(query: Result, refusal: string): Promise<void> {
  const data = check(await query);
  if (Array.isArray(data) && data.length === 0) throw new Error(refusal);
}
